import {
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { PickersShortcutsItem } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment"
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker"
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider"
import { DateRange } from "@mui/x-date-pickers-pro/models"
import { capitalize } from "lodash"
import moment, { Moment } from "moment"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  isValidReportDate,
  REPORT_MAX_DATE,
  REPORT_MIN_DATE,
} from "src/charts/chartsCommon"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import i18n from "src/i18n/i18n"
import { Sl2Btn } from "./Sl2Btn"

export type ReportBasicFilters = {
  dateField: string
  startDate: string
  endDate: string
  omniSearch: string
}
type OsReportBasicFiltersProps = {
  isLoading: boolean
  onChange: (value: { filters: ReportBasicFilters }) => void
}

type FiltersType = {
  dateField: string
  startDate: string
  endDate: string
  omniSearch: string
}

export const OsReportBasicFilters: FC<OsReportBasicFiltersProps> = ({
  onChange: onChangeProp,
  isLoading,
}) => {
  const { t } = useTranslation()

  const [filters, setFilters] = useSearchParamsState<FiltersType>("filters", {
    dateField: "sst_created",
    startDate: moment().subtract(1, "months").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    omniSearch: "",
  })

  const [localFilters, setLocalFilters] = useState<FiltersType>(filters)

  const handleDateRangeChange = (
    value: [moment.Moment | null, moment.Moment | null],
  ) => {
    // Keep out-of-range dates out of state entirely: the picker reports a
    // half-typed year (0000) as a "valid" moment, and submitting it returns a
    // confusing error from the backend (LOCKER-331).
    setLocalFilters((prev) => ({
      ...prev,
      startDate: isValidReportDate(value[0])
        ? value[0].format("YYYY-MM-DD")
        : "",
      endDate: isValidReportDate(value[1]) ? value[1].format("YYYY-MM-DD") : "",
    }))
  }

  const setDateRange = (startDate: moment.Moment, endDate: moment.Moment) => {
    setLocalFilters((prev) => ({
      ...prev,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    }))
  }

  const isDateRangeValid = useMemo(() => {
    const start = moment(localFilters.startDate, "YYYY-MM-DD", true)
    const end = moment(localFilters.endDate, "YYYY-MM-DD", true)
    return (
      isValidReportDate(start) &&
      isValidReportDate(end) &&
      start.isSameOrBefore(end, "day")
    )
  }, [localFilters.startDate, localFilters.endDate])

  const onApply = useCallback(() => {
    if (!isDateRangeValid) return
    setFilters(localFilters)
    onChangeProp({ filters: localFilters })
  }, [isDateRangeValid, localFilters, setFilters, onChangeProp])

  // apply on mount
  useEffect(onApply, [])

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "inline-block",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          bgcolor: "grey.100",
          py: 0,
          px: 2,
          width: "auto",
        }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body1">{t("filters")}</Typography>
      </Stack>
      <Stack direction="column" spacing={3} sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            label={t("omniSearchPlaceholder")}
            value={localFilters.omniSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setLocalFilters((prev: any) => ({
                ...prev,
                omniSearch: e.target.value,
              }))
            }
            fullWidth
            disabled={isLoading}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            select
            label={t("dateField")}
            value={localFilters.dateField}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                dateField: e.target.value,
              }))
            }
            fullWidth
            disabled={isLoading}
          >
            <MenuItem value=""> </MenuItem>
            <MenuItem value="sst_created">{t("sst_created")}</MenuItem>
            <MenuItem value="sst_delivery_time">
              {t("sst_delivery_time")}
            </MenuItem>
            <MenuItem value="sst_reception_time">
              {t("sst_reception_time")}
            </MenuItem>
          </TextField>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={i18n.language === "fr" ? "fr" : "en"}
          >
            <DateRangePicker
              localeText={{
                start: t("startDate"),
                end: t("endDate"),
              }}
              value={[
                localFilters.startDate ? moment(localFilters.startDate) : null,
                localFilters.endDate ? moment(localFilters.endDate) : null,
              ]}
              format="YYYY/MM/DD"
              onChange={handleDateRangeChange}
              disabled={isLoading}
              minDate={REPORT_MIN_DATE}
              maxDate={REPORT_MAX_DATE}
              slotProps={{
                shortcuts: {
                  items: [
                    {
                      label: capitalize(t("thisWeek")),
                      getValue: () => [
                        moment().startOf("week"),
                        moment().endOf("week"),
                      ],
                    },
                    {
                      label: capitalize(t("lastWeek")),
                      getValue: () => [
                        moment().subtract(1, "weeks").startOf("week"),
                        moment().subtract(1, "weeks").endOf("week"),
                      ],
                    },
                    {
                      label: capitalize(t("thisMonth")),
                      getValue: () => [
                        moment().startOf("month"),
                        moment().endOf("month"),
                      ],
                    },
                    {
                      label: capitalize(t("lastMonth")),
                      getValue: () => [
                        moment().subtract(1, "months").startOf("month"),
                        moment().subtract(1, "months").endOf("month"),
                      ],
                    },
                  ] as PickersShortcutsItem<DateRange<Moment>>[],
                },
              }}
              sx={{
                minWidth: "300px",
              }}
            />
          </LocalizationProvider>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Sl2Btn
            onClick={onApply}
            variant="contained"
            disabled={isLoading || !isDateRangeValid}
            sx={{
              width: "130px",
            }}
          >
            {isLoading && <CircularProgress size={16} />}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={"center"}
              // width="100px"
            >
              {t("apply")}
            </Stack>
          </Sl2Btn>
        </Stack>
      </Stack>
    </Paper>
  )
}
